import React, { useMemo, useState } from 'react';
import { Mail, Users, X } from 'lucide-react';
import Avatar from 'components/crm/ui/Avatar';
import TextField from 'components/crm/ui/TextField';
import useText from 'hooks/useText';

// The api's rule (contract § F): what it refuses with INVALID_ATTENDEES.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EMAIL_LENGTH = 200;
export const MAX_ATTENDEES = 50;
const MAX_SUGGESTIONS = 8;

export const isValidAttendeeEmail = (email) =>
  email.length <= MAX_EMAIL_LENGTH && EMAIL_FORMAT.test(email);

const normalize = (value) =>
  (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const memberName = (member) =>
  [member.firstname, member.lastname]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

// The api's people options carry no contactId: it sits at the end of their
// key, `p-<companyId>-<userId>`.
const contactIdOf = (option) =>
  option.contactId ??
  option.key?.match(/^p-[0-9a-f]{24}-([0-9a-f]{24})$/i)?.[1] ??
  null;

const attendeeName = (attendee) => attendee.displayName || attendee.email;

/**
 * The guests of an appointment: colleagues of the CRM (`team`, getTeam),
 * contacts of a company with an address (`contacts`, getContacts) or any
 * typed address (Enter). `value` is the whole list, each guest { userId } for
 * a colleague or { email, displayName, contactId } for anyone else, as the
 * api takes it; the entries it hands back (responseStatus…) stay untouched.
 * The owner of the calendar is never a guest: by `ownerId` (the api gives no
 * email for the owner) or by a typed `ownerEmail`.
 */
const AttendeesField = ({
  id = 'appointment-attendees',
  value = [],
  onChange,
  team = [],
  contacts = [],
  ownerEmail = '',
  ownerId = null,
}) => {
  const { text } = useText('appointmentFields');
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const owner = ownerEmail.toLowerCase();

  const isTaken = (candidate) =>
    value.some(
      (attendee) =>
        (candidate.userId && attendee.userId === candidate.userId) ||
        (candidate.email && attendee.email?.toLowerCase() === candidate.email),
    );

  const candidates = useMemo(
    () => [
      ...team
        .filter((member) => !ownerId || member.id !== ownerId)
        .map((member) => ({
          key: `u-${member.id}`,
          userId: member.id,
          email: (member.email || '').toLowerCase(),
          displayName: memberName(member),
          detail: text('attendeeColleague'),
          colleague: true,
        })),
      ...contacts
        .filter((option) => option.contact && option.email)
        .map((option) => ({
          key: option.key,
          email: option.email.toLowerCase(),
          displayName: option.contact,
          contactId: contactIdOf(option),
          detail: option.company
            ? `${option.email} · ${option.company}`
            : option.email,
        })),
    ],
    [team, contacts, text, ownerId],
  );

  const search = normalize(query.trim());
  const suggestions = search
    ? candidates
        .filter(
          (candidate) =>
            (!candidate.email || candidate.email !== owner) &&
            !isTaken(candidate) &&
            normalize(`${candidate.displayName} ${candidate.email}`).includes(
              search,
            ),
        )
        .filter(
          (candidate, index, list) =>
            !candidate.email ||
            list.findIndex((other) => other.email === candidate.email) ===
              index,
        )
        .slice(0, MAX_SUGGESTIONS)
    : [];
  const typedEmail = query.trim().toLowerCase();
  const canInviteTyped =
    isValidAttendeeEmail(typedEmail) &&
    typedEmail !== owner &&
    !isTaken({ email: typedEmail }) &&
    !suggestions.some((candidate) => candidate.email === typedEmail);
  const options = canInviteTyped
    ? [
        ...suggestions,
        {
          key: `e-${typedEmail}`,
          email: typedEmail,
          displayName: text('attendeeInvite', { email: typedEmail }),
          typed: true,
        },
      ]
    : suggestions;

  const add = (candidate) => {
    if (value.length >= MAX_ATTENDEES) {
      setError(text('attendeesTooMany'));
      return;
    }
    const attendee = candidate.colleague
      ? {
          userId: candidate.userId,
          email: candidate.email,
          displayName: candidate.displayName,
        }
      : {
          email: candidate.email,
          displayName: candidate.displayName || '',
          ...(candidate.contactId ? { contactId: candidate.contactId } : {}),
        };
    onChange([...value, attendee]);
    setQuery('');
    setError('');
  };

  const addTyped = () => {
    const email = query.trim().toLowerCase();
    if (!email) return;
    if (!isValidAttendeeEmail(email)) {
      setError(text('attendeeInvalidEmail'));
      return;
    }
    if (email === owner) {
      setError(text('attendeeIsOwner'));
      return;
    }
    if (isTaken({ email })) {
      setError(text('attendeeAlreadyInvited'));
      return;
    }
    // A known address keeps its name and its link to the contact.
    add(
      candidates.find(
        (candidate) => !candidate.colleague && candidate.email === email,
      ) ?? {
        email,
        displayName: '',
      },
    );
  };

  const remove = (index) =>
    onChange(value.filter((_, other) => other !== index));

  return (
    <div className="relative flex flex-col gap-2">
      <TextField
        id={id}
        label={text('attendeesLabel')}
        icon={Users}
        value={query}
        placeholder={text('attendeesPlaceholder')}
        autoComplete="off"
        role="combobox"
        aria-expanded={options.length > 0}
        aria-controls={`${id}-suggestions`}
        error={error}
        helper={text('attendeesHelper')}
        onChange={(event) => {
          setQuery(event.target.value);
          setError('');
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          addTyped();
        }}
      />
      {options.length > 0 && (
        <ul
          id={`${id}-suggestions`}
          role="listbox"
          aria-label={text('attendeesLabel')}
          className="max-h-64 overflow-y-auto rounded-xl border border-line bg-white p-1.5 shadow-pop"
        >
          {options.map((candidate) => (
            <li key={candidate.key} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => (candidate.typed ? addTyped() : add(candidate))}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-surfaceMuted"
              >
                {candidate.typed ? (
                  <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-surfaceMuted">
                    <Mail size={14} className="text-muted" />
                  </span>
                ) : (
                  <Avatar name={candidate.displayName} size={28} />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-ink">
                    {candidate.displayName}
                  </span>
                  {candidate.detail && (
                    <span className="block truncate text-xs text-muted">
                      {candidate.detail}
                    </span>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {value.length > 0 && (
        <ul
          className="flex flex-wrap gap-2"
          aria-label={text('attendeesLabel')}
        >
          {value.map((attendee, index) => (
            <li
              key={attendee.userId || attendee.email || index}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-line bg-white py-1 pl-1 pr-2 text-sm font-medium text-ink"
            >
              {attendee.displayName ? (
                <Avatar name={attendee.displayName} size={24} />
              ) : (
                <span className="inline-flex size-6 items-center justify-center rounded-full bg-surfaceMuted">
                  <Mail size={14} className="text-muted" />
                </span>
              )}
              <span className="truncate" title={attendee.email}>
                {attendeeName(attendee)}
              </span>
              <button
                type="button"
                aria-label={text('attendeeRemove', {
                  name: attendeeName(attendee),
                })}
                onClick={() => remove(index)}
                className="rounded-full p-0.5 text-muted hover:bg-surfaceMuted hover:text-ink"
              >
                <X size={14} strokeWidth={2} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AttendeesField;
